import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Loader2, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Wrench, Clock, X, Trash2, Calendar, RefreshCw, Bookmark, BookMarked, Play, Square, CheckSquare } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useBookmarks } from '../contexts/BookmarkContext';
import { useAnalysisGlobal } from '../contexts/AnalysisContext';
import { bidAgentsAPI } from '../services/api';
import './ElasticAgentReport.css';

const HISTORY_KEY = 'aiquire-search-history';
const MAX_HISTORY = 20;

const PRESETS = [
  { label: 'Closing This Week', question: 'Show me all tenders closing this week' },
  { label: 'New Today', question: 'Show me tenders published today' },
  { label: 'High Value (>$500K)', question: 'Show me active tenders with contract value over $500,000' },
  { label: 'Defence & Security', question: 'Show me Department of National Defence tenders' },
];

const PAGE_SIZE = 15;

const CHART_COLORS = ['#635bff', '#0a2540', '#2563eb', '#7c3aed', '#059669', '#d97706', '#dc2626', '#9ca3af'];

const formatCell = (column, value) => {
  if (value == null || value === '') return '--';
  if (column.includes('date')) {
    const d = new Date(value);
    if (!isNaN(d)) return d.toLocaleDateString('en-CA');
  }
  if (column === 'amount') {
    const num = parseFloat(value);
    if (!isNaN(num)) return `$${num.toLocaleString('en-CA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }
  const str = String(value);
  return str.length > 80 ? str.slice(0, 77) + '...' : str;
};

const columnLabel = (col) =>
  col.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const formatRelativeTime = (iso) => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-CA');
};

const TOOL_LABELS = {
  search_tenders_index: 'Searching tenders',
  search_pdf_chunks_index: 'Searching PDF documents',
  search_contract_history_index: 'Searching contract history',
  tenders_closing_soon: 'Finding closing deadlines',
  tenders_by_organization: 'Filtering by organization',
  high_value_tenders: 'Finding high-value tenders',
  recently_published_tenders: 'Finding new tenders',
  tender_stats_by_org: 'Aggregating statistics',
  contract_awards_by_supplier: 'Searching supplier awards',
  get_pricing_benchmark: 'Looking up pricing benchmarks',
  find_similar_contracts: 'Finding similar contracts',
  search_contracts_by_supplier: 'Searching by supplier',
  search_contracts_by_buyer: 'Searching by buyer',
  'platform.core.search': 'Searching',
};

// --- Dynamic insight derivation ---
const deriveInsights = (report) => {
  if (!report || !report.rows || report.rows.length === 0) return { stats: [], chart: null };

  const rows = report.rows;
  const cols = report.columns || [];
  const stats = [{ label: 'Total Results', value: rows.length }];

  // Detect org column
  const orgCol = cols.find(c => /organization|org/i.test(c));
  if (orgCol) {
    const unique = new Set(rows.map(r => r.values[orgCol]).filter(Boolean));
    stats.push({ label: 'Organizations', value: unique.size });
  }

  // Detect amount/value column
  const amtCol = cols.find(c => /amount|value|cost/i.test(c));
  if (amtCol) {
    const nums = rows.map(r => parseFloat(r.values[amtCol])).filter(n => !isNaN(n));
    if (nums.length > 0) {
      const total = nums.reduce((s, n) => s + n, 0);
      stats.push({ label: 'Total Value', value: `$${total.toLocaleString('en-CA', { maximumFractionDigits: 0 })}` });
      stats.push({ label: 'Avg Value', value: `$${Math.round(total / nums.length).toLocaleString('en-CA')}` });
    }
  }

  // Detect closing_date column
  const dateCol = cols.find(c => /closing.*date|close.*date/i.test(c));
  if (dateCol) {
    const now = Date.now();
    const daysAway = rows
      .map(r => {
        const d = new Date(r.values[dateCol]);
        return isNaN(d) ? null : Math.ceil((d - now) / 86400000);
      })
      .filter(d => d !== null && d >= 0);
    if (daysAway.length > 0) {
      const nearest = Math.min(...daysAway);
      stats.push({ label: 'Nearest Closing', value: `${nearest}d away` });
    }
  }

  // Chart auto-selection (first match wins)
  let chart = null;

  // 1. Organization pie chart
  if (!chart && orgCol) {
    const counts = {};
    rows.forEach(r => {
      const v = r.values[orgCol] || 'Unknown';
      counts[v] = (counts[v] || 0) + 1;
    });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    const top5 = sorted.slice(0, 5);
    const otherCount = sorted.slice(5).reduce((s, [, c]) => s + c, 0);
    const data = top5.map(([name, value]) => ({ name: name.length > 25 ? name.slice(0, 22) + '...' : name, value }));
    if (otherCount > 0) data.push({ name: 'Other', value: otherCount });
    if (data.length > 1) {
      chart = { type: 'pie', data, label: 'By Organization' };
    }
  }

  // 2. Category pie chart
  const catCol = cols.find(c => /category|type/i.test(c));
  if (!chart && catCol) {
    const counts = {};
    rows.forEach(r => {
      const v = r.values[catCol] || 'Uncategorized';
      counts[v] = (counts[v] || 0) + 1;
    });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    const top5 = sorted.slice(0, 5);
    const otherCount = sorted.slice(5).reduce((s, [, c]) => s + c, 0);
    const data = top5.map(([name, value]) => ({ name: name.length > 25 ? name.slice(0, 22) + '...' : name, value }));
    if (otherCount > 0) data.push({ name: 'Other', value: otherCount });
    if (data.length > 1) {
      chart = { type: 'pie', data, label: `By ${columnLabel(catCol)}` };
    }
  }

  // 3. Amount bar chart (top 10 by value)
  if (!chart && amtCol) {
    const titleCol = cols.find(c => /title|name/i.test(c));
    const labelCol = titleCol || cols[0];
    const withVal = rows
      .map(r => ({ name: String(r.values[labelCol] || '').slice(0, 30), value: parseFloat(r.values[amtCol]) }))
      .filter(d => !isNaN(d.value))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
    if (withVal.length > 1) {
      chart = { type: 'bar', data: withVal, label: `Top by ${columnLabel(amtCol)}` };
    }
  }

  // 4. Closing date bar chart (grouped by week)
  if (!chart && dateCol) {
    const weekMap = {};
    rows.forEach(r => {
      const d = new Date(r.values[dateCol]);
      if (isNaN(d)) return;
      const weekStart = new Date(d);
      weekStart.setDate(weekStart.getDate() - weekStart.getDay());
      const key = weekStart.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
      weekMap[key] = (weekMap[key] || 0) + 1;
    });
    const data = Object.entries(weekMap).map(([name, value]) => ({ name, value }));
    if (data.length > 1) {
      chart = { type: 'bar', data, label: 'Closing by Week' };
    }
  }

  return { stats, chart };
};

// --- Briefing helpers ---
const getDaysLeft = (closingDate) => {
  if (!closingDate) return null;
  const now = new Date();
  const close = new Date(closingDate);
  return Math.max(0, Math.ceil((close - now) / (1000 * 60 * 60 * 24)));
};

const getDaysClass = (days) => {
  if (days === null) return '';
  if (days <= 3) return 'days-badge--critical';
  if (days <= 7) return 'days-badge--warning';
  return 'days-badge--ok';
};

const truncate = (str, len = 60) => {
  if (!str) return '';
  return str.length > len ? str.slice(0, len) + '...' : str;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const ElasticAgentReport = ({ pipelineReport = null, pipelineLoading = false, onRefreshPipeline }) => {
  const navigate = useNavigate();
  const { isBookmarked, addBookmark, toggleBookmark } = useBookmarks();
  const { analyses, startAnalysis } = useAnalysisGlobal();

  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [sortCol, setSortCol] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [page, setPage] = useState(0);

  // Tabs
  const [activeTab, setActiveTab] = useState('pipeline');

  // Pipeline selection state
  const [selectedTenders, setSelectedTenders] = useState(new Set());
  const pipelineRows = useMemo(() => pipelineReport?.rows || [], [pipelineReport]);

  const toggleSelectTender = useCallback((tid) => {
    setSelectedTenders(prev => {
      const next = new Set(prev);
      if (next.has(tid)) next.delete(tid);
      else next.add(tid);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    const allSelected = pipelineRows.every(r => selectedTenders.has(r.id));
    if (allSelected) {
      setSelectedTenders(new Set());
    } else {
      setSelectedTenders(new Set(pipelineRows.map(r => r.id)));
    }
  }, [pipelineRows, selectedTenders]);

  const clearSelection = useCallback(() => setSelectedTenders(new Set()), []);

  const handleBulkSave = useCallback(() => {
    selectedTenders.forEach(tid => {
      if (!isBookmarked(tid)) {
        const row = pipelineRows.find(r => r.id === tid);
        if (row) {
          addBookmark({
            id: row.id,
            tender_id: row.id,
            title: row.values.title || row.id,
            organization: row.values.organization || '',
            closing_date: row.values.closing_date || null,
          });
        }
      }
    });
  }, [selectedTenders, pipelineRows, isBookmarked, addBookmark]);

  const handleBulkAnalyze = useCallback(() => {
    let delay = 0;
    selectedTenders.forEach(tid => {
      const a = analyses[tid];
      if (a?.status === 'running') return;
      setTimeout(() => startAnalysis(tid), delay);
      delay += 200;
    });
  }, [selectedTenders, analyses, startAnalysis]);

  const handleSingleAnalyze = useCallback((e, tid) => {
    e.stopPropagation();
    const a = analyses[tid];
    if (a?.status === 'running') return;
    startAnalysis(tid);
  }, [analyses, startAnalysis]);

  const handleToggleBookmark = useCallback((e, tender) => {
    e.stopPropagation();
    toggleBookmark(tender);
  }, [toggleBookmark]);

  // History state
  const [history, setHistory] = useState([]);
  const historyRef = useRef([]);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
      setHistory(stored);
      historyRef.current = stored;
    } catch { /* ignore corrupt data */ }
  }, []);

  const persistHistory = (entries) => {
    setHistory(entries);
    historyRef.current = entries;
    localStorage.setItem(HISTORY_KEY, JSON.stringify(entries));
  };

  const saveToHistory = useCallback((question, reportData) => {
    const entry = {
      id: Date.now().toString(),
      question,
      report: reportData,
      timestamp: new Date().toISOString(),
      rowCount: reportData.rows?.length || 0,
    };
    const updated = [entry, ...historyRef.current].slice(0, MAX_HISTORY);
    persistHistory(updated);
  }, []);

  const loadFromHistory = (entry) => {
    setQuery(entry.question);
    setReport(entry.report);
    setPhase('done');
    setError(null);
    setLoading(false);
    setSortCol(null);
    setSortAsc(true);
    setPage(0);
    setActiveTab('results');
  };

  const deleteFromHistory = (id) => {
    persistHistory(history.filter((e) => e.id !== id));
  };

  const clearHistory = () => {
    persistHistory([]);
  };

  // Streaming state
  const [statusMsg, setStatusMsg] = useState('');
  const [toolCalls, setToolCalls] = useState([]);
  const [streamingText, setStreamingText] = useState('');
  const [phase, setPhase] = useState('idle');
  const streamTextRef = useRef('');

  const runReport = useCallback(async (question) => {
    setLoading(true);
    setError(null);
    setReport(null);
    setSortCol(null);
    setSortAsc(true);
    setPage(0);
    setStatusMsg('');
    setToolCalls([]);
    setStreamingText('');
    streamTextRef.current = '';
    setPhase('agent');
    setActiveTab('results');

    try {
      await bidAgentsAPI.streamElasticReport(question, {
        onStatus: (data) => {
          setStatusMsg(data.message || '');
          if (data.message?.includes('Structuring')) {
            setPhase('structuring');
          }
        },
        onToolCall: (data) => {
          const label = TOOL_LABELS[data.tool] || data.tool;
          setToolCalls((prev) => [...prev.slice(-4), label]);
          setStatusMsg(label + '...');
        },
        onMessageChunk: (data) => {
          if (data.content) {
            streamTextRef.current += data.content;
            setStreamingText(streamTextRef.current);
          }
        },
        onMessageComplete: () => {
          setStatusMsg('Agent finished \u2014 structuring results...');
          setPhase('structuring');
        },
        onReportComplete: (data) => {
          setReport(data);
          setPhase('done');
          setLoading(false);
          saveToHistory(question, data);
        },
        onError: (data) => {
          setError(data.message || 'Report generation failed');
          setPhase('idle');
          setLoading(false);
        },
      });

      setLoading(false);
      if (!report) setPhase('done');
    } catch (err) {
      setError(err.message || 'Report generation failed');
      setPhase('idle');
      setLoading(false);
    }
  }, [report, saveToHistory]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) runReport(query.trim());
  };

  const handlePreset = (question) => {
    setQuery(question);
    runReport(question);
  };

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortAsc(!sortAsc);
    } else {
      setSortCol(col);
      setSortAsc(true);
    }
    setPage(0);
  };

  // Sort rows
  let rows = report?.rows || [];
  if (sortCol) {
    rows = [...rows].sort((a, b) => {
      const va = a.values[sortCol] ?? '';
      const vb = b.values[sortCol] ?? '';
      const cmp = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
      return sortAsc ? cmp : -cmp;
    });
  }

  // Paginate
  const totalPages = Math.ceil(rows.length / PAGE_SIZE);
  const pagedRows = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Dynamic insights
  const insights = useMemo(() => deriveInsights(report), [report]);

  // Pipeline row status
  const getRowStatus = useCallback((row) => {
    const tid = row.id;
    const analysis = analyses[tid];
    if (analysis?.status === 'running') return { label: 'Analyzing...', cls: 'status-badge--analyzing' };
    if (analysis?.status === 'complete') {
      const rec = analysis.phases?.phase4?.recommendation
        || analysis.phases?.phase3?.recommendation
        || '';
      const upper = rec.toUpperCase();
      if (upper === 'BID') return { label: 'BID', cls: 'status-badge--bid' };
      if (upper === 'CONSIDER') return { label: 'CONSIDER', cls: 'status-badge--consider' };
      if (upper === 'PASS') return { label: 'PASS', cls: 'status-badge--pass' };
      return { label: 'Analyzed', cls: 'status-badge--saved' };
    }
    if (isBookmarked(tid)) return { label: 'Saved', cls: 'status-badge--saved' };
    return { label: 'Not Tracked', cls: 'status-badge--not-tracked' };
  }, [analyses, isBookmarked]);

  const closingSoon7d = pipelineRows.filter(r => {
    const d = getDaysLeft(r.values.closing_date);
    return d !== null && d <= 7;
  }).length;

  const tracked = pipelineRows.filter(r => isBookmarked(r.id)).length;
  const analyzed = pipelineRows.filter(r => {
    const a = analyses[r.id];
    return a?.status === 'complete';
  }).length;

  return (
    <div className="elastic-report">
      <div className="report-header">
        <h2>Intelligence Hub</h2>
        <p className="report-subtitle">Daily pipeline briefing, on-demand reports, and natural-language search</p>
      </div>

      <div className="preset-buttons">
        {PRESETS.map((p) => (
          <button
            key={p.label}
            className="preset-btn"
            onClick={() => handlePreset(p.question)}
            disabled={loading}
          >
            {p.label}
          </button>
        ))}
      </div>

      <form className="report-search-form" onSubmit={handleSubmit}>
        <div className="report-search-input-wrap">
          <Search size={18} className="report-search-icon" />
          <input
            type="text"
            className="report-search-input"
            placeholder="e.g. Show me RCMP tenders over $100K closing next month"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={loading}
          />
        </div>
        <button type="submit" className="report-run-btn" disabled={loading || !query.trim()}>
          {loading ? <Loader2 size={16} className="spinning" /> : 'Run Report'}
        </button>
      </form>

      {error && <div className="report-error">{error}</div>}

      {/* Tab bar */}
      <div className="hub-tabs">
        <button
          className={`hub-tab${activeTab === 'pipeline' ? ' active' : ''}`}
          onClick={() => setActiveTab('pipeline')}
        >
          <Calendar size={14} /> Pipeline
        </button>
        <button
          className={`hub-tab${activeTab === 'results' ? ' active' : ''}`}
          onClick={() => setActiveTab('results')}
        >
          <Search size={14} /> Results
        </button>
        <button
          className={`hub-tab${activeTab === 'history' ? ' active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          <Clock size={14} /> History
          {history.length > 0 && <span className="hub-tab-badge">{history.length}</span>}
        </button>
      </div>

      {/* Tab content */}
      <div className="hub-tab-content">

        {/* ===== PIPELINE TAB ===== */}
        {activeTab === 'pipeline' && (
          <div className="pipeline-briefing">
            <div className="briefing-header">
              <div className="briefing-title-row">
                <Calendar size={20} />
                <h2>{pipelineReport?.report_title || '30-Day Pipeline'}</h2>
              </div>
              <div className="briefing-meta">
                <span className="briefing-date">
                  Updated: {pipelineReport?.generated_at
                    ? new Date(pipelineReport.generated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
                    : '--'}
                </span>
                {onRefreshPipeline && (
                  <button className="briefing-refresh" onClick={onRefreshPipeline} disabled={pipelineLoading}>
                    <RefreshCw size={14} className={pipelineLoading ? 'spinning' : ''} />
                  </button>
                )}
              </div>
            </div>

            {pipelineReport?.narrative && !pipelineLoading && (
              <div className="report-narrative" style={{ marginBottom: '1rem' }}>{pipelineReport.narrative}</div>
            )}

            <div className="briefing-summary">
              <div className="briefing-stat">
                <div className="briefing-stat-value briefing-stat-critical">{closingSoon7d}</div>
                <div className="briefing-stat-label">Closing Soon (7d)</div>
              </div>
              <div className="briefing-stat">
                <div className="briefing-stat-value">{pipelineRows.length}</div>
                <div className="briefing-stat-label">Total in Pipeline</div>
              </div>
              <div className="briefing-stat">
                <div className="briefing-stat-value briefing-stat-tracked">{tracked}</div>
                <div className="briefing-stat-label">Tracked</div>
              </div>
              <div className="briefing-stat">
                <div className="briefing-stat-value briefing-stat-analyzed">{analyzed}</div>
                <div className="briefing-stat-label">Analyzed</div>
              </div>
            </div>

            {pipelineLoading ? (
              <div className="briefing-loading">
                <Loader2 size={20} className="spinning" />
                <span>Generating pipeline report...</span>
              </div>
            ) : pipelineRows.length === 0 ? (
              <div className="briefing-empty">No tenders found. Click refresh to regenerate the report.</div>
            ) : (
              <>
                <div className="briefing-table-wrap">
                  <table className="briefing-table">
                    <thead>
                      <tr>
                        <th className="briefing-th-checkbox">
                          <button className="pipeline-checkbox-btn" onClick={(e) => { e.stopPropagation(); toggleSelectAll(); }}>
                            {pipelineRows.length > 0 && pipelineRows.every(r => selectedTenders.has(r.id))
                              ? <CheckSquare size={16} />
                              : <Square size={16} />}
                          </button>
                        </th>
                        <th>Title</th>
                        <th>Organization</th>
                        <th>Closes</th>
                        <th>Days Left</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pipelineRows.map((row) => {
                        const tid = row.id;
                        const closingDate = row.values.closing_date;
                        const days = getDaysLeft(closingDate);
                        const status = getRowStatus(row);
                        const title = row.values.title || tid;
                        const org = row.values.organization || '';
                        const saved = isBookmarked(tid);
                        const analysisState = analyses[tid];
                        const isRunning = analysisState?.status === 'running';
                        const isSelected = selectedTenders.has(tid);

                        return (
                          <tr
                            key={tid}
                            className={`briefing-row${isSelected ? ' briefing-row--selected' : ''}`}
                            onClick={() => navigate(`/tender/${tid}`)}
                          >
                            <td className="briefing-td-checkbox">
                              <button className="pipeline-checkbox-btn" onClick={(e) => { e.stopPropagation(); toggleSelectTender(tid); }}>
                                {isSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                              </button>
                            </td>
                            <td className="briefing-title-cell">{truncate(title, 55)}</td>
                            <td className="briefing-org">{truncate(org, 30)}</td>
                            <td className="briefing-date-cell">{formatDate(closingDate)}</td>
                            <td>
                              {days !== null ? (
                                <span className={`days-badge ${getDaysClass(days)}`}>{days}d</span>
                              ) : (
                                <span className="days-badge">--</span>
                              )}
                            </td>
                            <td>
                              <span className={`status-badge ${status.cls}`}>{status.label}</span>
                            </td>
                            <td className="briefing-actions">
                              <button
                                className={`pipeline-action-btn${saved ? ' pipeline-action-btn--active' : ''}`}
                                onClick={(e) => handleToggleBookmark(e, { id: tid, tender_id: tid, title, organization: org, closing_date: closingDate })}
                                title={saved ? 'Remove bookmark' : 'Save tender'}
                              >
                                {saved ? <BookMarked size={14} /> : <Bookmark size={14} />}
                              </button>
                              <button
                                className={`pipeline-action-btn${isRunning ? ' pipeline-action-btn--running' : ''}`}
                                onClick={(e) => handleSingleAnalyze(e, tid)}
                                title={isRunning ? 'Analysis running...' : 'Run bid analysis'}
                                disabled={isRunning}
                              >
                                {isRunning ? <Loader2 size={14} className="spinning" /> : <Play size={14} />}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Bulk action bar */}
                {selectedTenders.size > 0 && (
                  <div className="pipeline-bulk-bar">
                    <span className="pipeline-bulk-count">{selectedTenders.size} selected</span>
                    <button className="pipeline-bulk-btn pipeline-bulk-btn--save" onClick={handleBulkSave}>
                      <BookMarked size={14} /> Save Selected
                    </button>
                    <button className="pipeline-bulk-btn pipeline-bulk-btn--analyze" onClick={handleBulkAnalyze}>
                      <Play size={14} /> Analyze Selected
                    </button>
                    <button className="pipeline-bulk-btn pipeline-bulk-btn--clear" onClick={clearSelection}>
                      <X size={14} /> Clear
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ===== RESULTS TAB ===== */}
        {activeTab === 'results' && (
          <div className="results-tab-content">
            {/* Streaming progress */}
            {loading && (
              <div className="report-stream-progress">
                <div className="stream-status-bar">
                  <Loader2 size={16} className="spinning" />
                  <span className="stream-status-text">{statusMsg || 'Connecting...'}</span>
                </div>

                {toolCalls.length > 0 && (
                  <div className="stream-tools">
                    {toolCalls.map((tool, i) => (
                      <span key={i} className="stream-tool-badge">
                        <Wrench size={12} /> {tool}
                      </span>
                    ))}
                  </div>
                )}

                {streamingText && phase === 'agent' && (
                  <div className="stream-preview">
                    {streamingText.slice(-600)}
                    <span className="stream-cursor">|</span>
                  </div>
                )}

                {phase === 'structuring' && (
                  <div className="stream-structuring">
                    <Loader2 size={16} className="spinning" />
                    <span>Structuring results into report table...</span>
                  </div>
                )}
              </div>
            )}

            {/* Final report */}
            {report && !loading && (
              <div className="report-results">
                {/* Dynamic stat cards */}
                {insights.stats.length > 0 && (
                  <div className="insight-stats">
                    {insights.stats.map((s, i) => (
                      <div key={i} className="insight-stat-card">
                        <div className="insight-stat-value">{s.value}</div>
                        <div className="insight-stat-label">{s.label}</div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Auto chart */}
                {insights.chart && (
                  <div className="insight-chart-wrap">
                    <div className="insight-chart-title">{insights.chart.label}</div>
                    {insights.chart.type === 'pie' ? (
                      <ResponsiveContainer width="100%" height={260}>
                        <PieChart>
                          <Pie
                            data={insights.chart.data}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={90}
                            label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                          >
                            {insights.chart.data.map((_, idx) => (
                              <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={insights.chart.data}>
                          <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-30} textAnchor="end" height={60} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Bar dataKey="value" fill="#635bff" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                )}

                <h3 className="report-title">{report.report_title}</h3>
                <div className="report-narrative">{report.narrative}</div>

                {report.rows.length > 0 && (
                  <>
                    <div className="report-meta">
                      {report.total_hits} result{report.total_hits !== 1 ? 's' : ''}
                      {report.total_hits > rows.length && ` (showing ${rows.length})`}
                    </div>

                    <div className="report-table-wrap">
                      <table className="report-table">
                        <thead>
                          <tr>
                            {report.columns.map((col) => (
                              <th key={col} onClick={() => handleSort(col)} className="sortable-th">
                                {columnLabel(col)}
                                {sortCol === col && (
                                  sortAsc ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                                )}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {pagedRows.map((row, i) => (
                            <tr key={row.id || i}>
                              {report.columns.map((col) => (
                                <td key={col}>
                                  {col === 'title' && row.id ? (
                                    <Link
                                      to={`/tender/${encodeURIComponent(row.id)}`}
                                      className="tender-link"
                                    >
                                      {formatCell(col, row.values[col])}
                                    </Link>
                                  ) : (
                                    formatCell(col, row.values[col])
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {totalPages > 1 && (
                      <div className="report-pagination">
                        <button
                          onClick={() => setPage(page - 1)}
                          disabled={page === 0}
                          className="page-btn"
                        >
                          <ChevronLeft size={16} /> Previous
                        </button>
                        <span className="page-info">
                          Page {page + 1} of {totalPages}
                        </span>
                        <button
                          onClick={() => setPage(page + 1)}
                          disabled={page >= totalPages - 1}
                          className="page-btn"
                        >
                          Next <ChevronRight size={16} />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Empty state */}
            {!report && !loading && (
              <div className="results-empty">
                <Search size={32} strokeWidth={1.5} />
                <p>Run a search above to see results here</p>
              </div>
            )}
          </div>
        )}

        {/* ===== HISTORY TAB ===== */}
        {activeTab === 'history' && (
          <div className="history-panel">
            <div className="history-panel-header">
              <span className="history-panel-title">Recent Searches</span>
              {history.length > 0 && (
                <button className="history-clear" onClick={clearHistory}>
                  <Trash2 size={12} /> Clear All
                </button>
              )}
            </div>
            {history.length === 0 ? (
              <div className="history-empty">No saved searches yet. Run a search and it will appear here.</div>
            ) : (
              <div className="history-list">
                {history.map((entry) => (
                  <div key={entry.id} className="history-entry" onClick={() => loadFromHistory(entry)}>
                    <div className="history-question">{entry.question}</div>
                    <div className="history-meta">
                      {entry.rowCount} result{entry.rowCount !== 1 ? 's' : ''}
                      {' \u00b7 '}
                      {formatRelativeTime(entry.timestamp)}
                    </div>
                    <button
                      className="history-delete-btn"
                      onClick={(e) => { e.stopPropagation(); deleteFromHistory(entry.id); }}
                      title="Remove"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ElasticAgentReport;
